'use client';

import {
	BigNum,
	BN,
	getTokenAmount,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import { BankBalanceUI, MarketId, getUserKey } from '@velocity-exchange/common';
import useVelocityClient from './useVelocityClient';
import useVelocityClientIsReady from './useVelocityClientIsReady';
import useSubaccountCollateralBalances from './useSubaccountCollateralBalances';
import { useEffect, useMemo, useRef } from 'react';
import useVelocityAccountStore, {
	AccountSpotBalance,
} from 'src/stores/useVelocityAccountsStore';
import useGetOraclePriceForMarket from './useGetOraclePriceForMarket';
import { useSpotMarketsSortedByMarketIndex } from './spotMarkets/useSpotMarketsSortedByMarketIndex';
import { useLiveMarketsSnapshot } from 'src/stores/marketLiveState';

const EMPTY_SPOT_BALANCES: AccountSpotBalance[] = [];

const checkIfBalancesAreEqual = (balances1: BN[], balances2: BN[]): boolean => {
	if (balances1.length !== balances2.length) {
		return false;
	}

	return balances1.every((balance1, index) => {
		return balance1.eq(balances2[index]);
	});
};

export const useSyncAccountSpotBalances = () => {
	const velocityClientIsReady = useVelocityClientIsReady();
	const velocityClient = useVelocityClient();
	const setAccountsStore = useVelocityAccountStore((s) => s.set);
	const currentUserKey = useVelocityAccountStore((s) => s.currentUserKey);
	const spotMarketsSortedByMarketIndex = useSpotMarketsSortedByMarketIndex();

	const previousUserBankBalances = useRef<BankBalanceUI[]>([]);
	const userBankBalances = useSubaccountCollateralBalances(
		currentUserKey,
		true
	);

	const getOraclePriceForMarket = useGetOraclePriceForMarket();

	// Get the unique market indices from user's balances
	const userMarketIndices = useMemo(() => {
		const balancesToCheck =
			userBankBalances.length > 0
				? userBankBalances
				: previousUserBankBalances.current;
		return [...new Set(balancesToCheck.map((b) => b.marketIndex))];
	}, [userBankBalances]);

	// Reactive live-price snapshot for the user's spot markets. Replaces the
	// old essential-interval oracle-price string-key polling: the module flushes
	// at 250ms and keeps each market's snapshot referentially stable when
	// unchanged, so this only changes (and re-syncs the effect below) when a
	// user market's live price actually moves.
	const userMarketsPriceSnapshot = useLiveMarketsSnapshot(
		useMemo(
			() => userMarketIndices.map((i) => MarketId.createSpotMarket(i)),
			[userMarketIndices]
		)
	);

	// Memoised hook to make sure that we only format and update bank balances if there is actually a change in the balances
	const newBankBalances: BankBalanceUI[] = useMemo(() => {
		if (!velocityClientIsReady || !velocityClient || !currentUserKey) return [];

		const bankBalancesInStore =
			useVelocityAccountStore.getState().accounts[currentUserKey]?.spotBalances;

		const balancesAreEqual = bankBalancesInStore
			? checkIfBalancesAreEqual(
					bankBalancesInStore.map((b) => b.balance.val),
					userBankBalances.map((b) => b.scaledBalance)
				)
			: false;

		if (balancesAreEqual) {
			return previousUserBankBalances.current;
		}

		previousUserBankBalances.current = userBankBalances;
		return userBankBalances;
	}, [velocityClientIsReady, velocityClient, userBankBalances]);

	// Sync when balances change OR when oracle prices for user's markets change
	useEffect(() => {
		if (!velocityClientIsReady || !velocityClient) return;

		const formattedSpotBalances = formatSpotBalances(
			newBankBalances.length > 0
				? newBankBalances
				: previousUserBankBalances.current
		);

		setSpotBalancesInStore(
			formattedSpotBalances.filter(Boolean) as AccountSpotBalance[]
		);
	}, [
		velocityClientIsReady,
		velocityClient,
		newBankBalances,
		userMarketsPriceSnapshot,
	]);

	const formatSpotBalances = (bankBalancesToFormat: BankBalanceUI[]) => {
		if (spotMarketsSortedByMarketIndex.length === 0) return [];
		return bankBalancesToFormat
			.map((bankBalance) => {
				const spotAccount = velocityClient?.getSpotMarketAccount(
					bankBalance.marketIndex
				);
				if (
					!spotMarketsSortedByMarketIndex[bankBalance.marketIndex] ||
					!spotAccount
				)
					return null;
				const assetPrecision =
					spotMarketsSortedByMarketIndex[bankBalance.marketIndex].precisionExp;

				const baseBalance = BigNum.from(
					getTokenAmount(
						bankBalance.scaledBalance,
						spotAccount,
						bankBalance.balanceType
					),
					assetPrecision
				).shiftTo(assetPrecision);

				const marketId = MarketId.createSpotMarket(bankBalance.marketIndex);

				const oraclePrice = getOraclePriceForMarket(marketId);
				if (!oraclePrice) return null;

				const quoteValue = baseBalance
					.shiftTo(PRICE_PRECISION_EXP)
					.mul(oraclePrice)
					.shiftTo(QUOTE_PRECISION_EXP);

				return {
					asset: spotMarketsSortedByMarketIndex[bankBalance.marketIndex],
					balance: baseBalance,
					balanceType: bankBalance.balanceType,
					quoteValue,
					accountId: bankBalance.accountId,
					accountName: bankBalance.accountName,
					accountKey: getUserKey(
						bankBalance.accountId,
						bankBalance.accountAuthority
					),
				};
			})
			.filter(Boolean);
	};

	const setSpotBalancesInStore = (spotBalances: AccountSpotBalance[]) => {
		const groupedAccountsBalances = spotBalances.reduce<
			Record<string, AccountSpotBalance[]>
		>((acc, spotBalance) => {
			const { accountKey } = spotBalance;

			if (!acc[accountKey]) {
				acc[accountKey] = [];
			}

			acc[accountKey].push(spotBalance);

			return acc;
		}, {});

		setAccountsStore((s) => {
			const accountKeys = Object.keys(s.accounts);
			accountKeys.forEach((accountKey) => {
				if (!s.accounts[accountKey]) return;

				s.accounts[accountKey].spotBalances =
					groupedAccountsBalances[accountKey] ?? EMPTY_SPOT_BALANCES;
			});
		});
	};
};
